/**
 * Shares the authenticated API client, session helpers and plan entitlements.
 */

import { createContext, useContext } from "react";

import type { InternalCaseApiClient } from "@libro-reclamos/api-client/InternalCases";
import type { CompanyProfile } from "@libro-reclamos/contracts/Platform";
import type { CompanyEntitlements } from "@libro-reclamos/contracts/Plans";

export interface WorkspaceContextValue {
  readonly canManageUsers: boolean;
  readonly client: InternalCaseApiClient;
  /**
   * The active company's own profile. `undefined` while it loads; `null` when
   * the principal is not the company administrator (or has no company).
   */
  readonly companyProfile: CompanyProfile | null | undefined;
  /**
   * `null` while the plan is still loading or when the signed-in principal has
   * no company (a platform operator). Consumers must treat it as "not yet known"
   * and not as "nothing is included".
   */
  readonly entitlements: CompanyEntitlements | null;
  readonly expireSession: () => void;
  readonly isPlatformOperator: boolean;
  readonly openCase: (publicId: string) => void;
  readonly revision: number;
  /** Replaces the profile after the administrator saves part of it. */
  readonly setCompanyProfile: (profile: CompanyProfile) => void;
}

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);

/**
 * Returns the authenticated workspace context or throws outside the shell.
 */
export function useWorkspace(): WorkspaceContextValue {
  const value = useContext(WorkspaceContext);
  if (value === null) {
    throw new Error("Workspace context is only available inside the authenticated shell.");
  }
  return value;
}

export const WorkspaceProvider = WorkspaceContext.Provider;
